// Spiegelei Studio 的所有 App：首页卡片、付款说明和 /checkout 的返回链接都用这份列表。
// id 和各 App 结账时写进 Paddle custom_data.app 的值一致（dracochinese、dracolanguage）

// 有网页订阅的 App 各有正式站和测试站：测试站点配置成对应 App 的测试站，
// 这样 /checkout 付款后的返回链接和价格、条款链接都指向同一套环境。不配置时用正式网址
const siteUrl = (value: string | undefined, fallback: string) => (value || fallback).replace(/\/+$/, '');
const DRACOCHINESE_URL = siteUrl(process.env.REACT_APP_DRACOCHINESE_URL, 'https://www.dracochinese.com');
const DRACOLANGUAGE_URL = siteUrl(process.env.REACT_APP_DRACOLANGUAGE_URL, 'https://dracolanguage.com');

export type AppStatus = 'live' | 'coming-soon' | 'in-development';

export interface WebBilling {
  // 默认价格（和各 App 官网的默认显示价一致）；结账时 Paddle 按访客所在国家显示当地价格
  monthlyPrice: string;
  yearlyPrice: string;
  pricingUrl: string;
  termsUrl: string;
  privacyUrl: string;
  refundUrl: string;
  refundSummary: string;
}

export interface AppInfo {
  id: string;
  title: string;
  logo: string;
  status: AppStatus;
  platforms: string;
  description: string;
  features: string[];
  websiteUrl?: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
  chromeStoreUrl?: string;
  // 有网页订阅（Paddle）的 App 才有
  webBilling?: WebBilling;
}

export const STATUS_LABELS: Record<AppStatus, string> = {
  live: 'Available now',
  'coming-soon': 'Coming soon',
  'in-development': 'In development',
};

export const apps: AppInfo[] = [
  {
    id: 'dracochinese',
    title: 'DracoChinese',
    logo: '/images/draco-chinese-logo.webp',
    status: 'live',
    platforms: 'iOS, Android, Chrome extension, Web',
    description:
      'Learn Chinese the modern way with interactive article reading, gamified missions, stroke order practice, and flashcard memorization. Available in 11 languages.',
    features: [
      'Interactive article reading with instant word lookup',
      'Gamified missions with progressive levels',
      'Stroke order practice with real-time feedback',
      'Chrome extension for learning Chinese with YouTube',
    ],
    websiteUrl: DRACOCHINESE_URL,
    appStoreUrl: 'https://apps.apple.com/app/dracochinese/id6751784292',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.spiegeleistudio.dracochinese',
    chromeStoreUrl: 'https://chromewebstore.google.com/detail/dracochinese-youtube-piny/ckokakdhhjimknhmkcdlnmofdpicielo',
    webBilling: {
      // 和 draco-chinese-website 的 src/utils/pricing.js 一致
      monthlyPrice: '€2.99',
      yearlyPrice: '€19.99',
      pricingUrl: `${DRACOCHINESE_URL}/#pricing`,
      termsUrl: `${DRACOCHINESE_URL}/terms-of-use`,
      privacyUrl: `${DRACOCHINESE_URL}/privacy-policy`,
      refundUrl: `${DRACOCHINESE_URL}/refund-policy`,
      refundSummary: '14-day money-back guarantee',
    },
  },
  {
    id: 'dracochess',
    title: 'DracoChess',
    logo: '/images/draco-chess-logo.png',
    status: 'live',
    platforms: 'iPhone, iPad, Mac',
    description:
      'Learn chess through adventure: an RPG-style quest where you battle monsters from a Slime up to a Dragon boss, each one teaching a new chess skill. Built for kids and beginners.',
    features: [
      'Adventure mode with monsters of rising strength',
      'Post-game review that explains hanging pieces and missed forks',
      'Lessons and puzzles on tactics, openings, and endgames',
      'No account, no ads, works offline',
    ],
    appStoreUrl: 'https://apps.apple.com/app/dracochess/id6814472557',
  },
  {
    id: 'dracolanguage',
    title: 'DracoLanguage',
    logo: '/images/draco-language-logo.webp',
    status: 'coming-soon',
    platforms: 'Chrome extension',
    description:
      'A Chrome extension for learning languages with YouTube, Netflix, Disney+, TED, and Coursera: an interactive transcript, word lookups, and AI explanations of grammar and vocabulary.',
    features: [
      'Interactive transcript next to the video',
      'Instant word lookups and saved vocabulary',
      'AI explanations of grammar and vocabulary',
      '11 learning languages',
    ],
    websiteUrl: DRACOLANGUAGE_URL,
    webBilling: {
      // 和 draco-language-website 的 src/lib/product.ts 一致（美元，不含税）
      monthlyPrice: '$3.99',
      yearlyPrice: '$24.99',
      pricingUrl: `${DRACOLANGUAGE_URL}/pricing`,
      termsUrl: `${DRACOLANGUAGE_URL}/terms`,
      privacyUrl: `${DRACOLANGUAGE_URL}/privacy`,
      refundUrl: `${DRACOLANGUAGE_URL}/refund`,
      refundSummary: '14-day money-back guarantee',
    },
  },
  {
    id: 'dracogerman',
    title: 'DracoGerman',
    logo: '/images/draco-german-logo.png',
    status: 'in-development',
    platforms: 'iOS',
    description:
      'An app for learning German with pronunciation scoring, live listening with AI analysis, and spaced-repetition review of vocabulary.',
    features: [],
  },
  {
    id: 'dracotravel',
    title: 'DracoTravel',
    logo: '/images/draco-travel-logo.png',
    status: 'in-development',
    platforms: 'iOS',
    description:
      'A travel companion for international visitors in mainland China: find places, talk with locals through quick phrases and live translation, and scan Chinese text such as menus and signs.',
    features: [],
  },
];

export const findApp = (id: string | undefined) => apps.find((app) => app.id === id);
